import React, { createContext, useContext, useMemo, useEffect, useState, useCallback } from 'react';
import { last, find, reduce, filter, truncate } from 'lodash';
import { useLocation, useHistory, matchPath } from 'react-router-dom';

import {
  useAuth,
  getNotificationsUrl,
  MONUMENT_SYSTEM_CHAT_USER_ID,
  toast,
  useRequest,
  useAppChat,
} from '@monument/core';

// Should be kept in sync with backend PatientNotificationType
export const PatientNotificationType = {
  PatientInsuranceRequested: 'insurance-requested',
};

const isSystemUser = userId => userId === MONUMENT_SYSTEM_CHAT_USER_ID;
const getCompanionFromChannel = (channel, currentUser) =>
  find(channel.state.members, ({ user }) => user.id !== currentUser.accountId);
const getNewestMessageFromChannel = channel => {
  return last(filter(channel.state.messages, m => !isSystemUser(m.user.id)));
};

const NotificationsContext = createContext();
export const useNotifications = () => useContext(NotificationsContext);

const getChatNotificationsFromChannels = (chatChannels, currentUser) => {
  return reduce(
    chatChannels,
    (acc, channel) => {
      const newestMessage = getNewestMessageFromChannel(channel);
      if (!newestMessage) {
        return acc;
      }

      acc.push({
        isNewMessage: !!channel.countUnread(),
        fromUser: getCompanionFromChannel(channel, currentUser),
        channelId: channel.id,
        message: newestMessage.text,
        messageId: newestMessage.id,
        createdAt: newestMessage.created_at,
      });

      return acc;
    },
    [],
  );
};

const useChatNotifications = currentUser => {
  const { channels } = useAppChat();
  const chatChannels = channels?.oneToOne;
  const location = useLocation();
  const history = useHistory();

  const [newMessage, setNewMessage] = useState();
  const [chatNotifications, setChatNotifications] = useState([]);

  const isUserOnChatPage = useMemo(
    () => !!matchPath(location.pathname, { path: '/care/chat' }),
    [location.pathname],
  );

  const messagesChangeListener = useCallback(
    event => {
      // make sure when the sender user reads a message it doesn't
      // also remove the notification for the receiving user.
      // For example, this happens when the sender leaves their browser tab
      // and then returns making it active again.
      if (event.type === 'message.read' && event.user.id !== currentUser.accountId) {
        return;
      }

      if (isSystemUser(event.user.id)) {
        return;
      }

      if (event.type === 'message.new') {
        setNewMessage({ message: event.message, channelId: event.channel_id });
      }

      setChatNotifications(getChatNotificationsFromChannels(chatChannels, currentUser));
    },
    [chatChannels, currentUser],
  );

  useEffect(() => {
    if (!newMessage || isUserOnChatPage) {
      return;
    }
    toast.info({
      title: `New message from ${newMessage.message.user.name}`,
      description: truncate(newMessage.message.text, { separator: ' ', length: 100 }),
      position: 'bottom-right',
      onClick: () => {
        history.push('/care/chat', { activeChatUser: newMessage.message.user.id });
      },
      duration: Infinity,
    });
  }, [newMessage]);

  useEffect(() => {
    if (!chatChannels) {
      return;
    }

    setChatNotifications(getChatNotificationsFromChannels(chatChannels, currentUser));

    chatChannels.forEach(channel => {
      channel.on('message.new', messagesChangeListener);
      channel.on('message.updated', messagesChangeListener);
      channel.on('message.read', messagesChangeListener);
      channel.on('message.deleted', messagesChangeListener);
    });
  }, [chatChannels]);

  return {
    chatNotifications,
  };
};

const useAccountNotifications = () => {
  const { isAuthenticated } = useAuth();

  const {
    data: notifications,
    loading: loadingNotifications,
    refresh: refreshBackendNotifications,
  } = useRequest(getNotificationsUrl(), {
    ready: isAuthenticated,
  });

  const refetchNotifications = () => {
    refreshBackendNotifications();
  };

  return {
    notifications,
    loadingNotifications,
    refetchNotifications,
  };
};

export const NotificationsProvider = ({ children }) => {
  const { user } = useAuth();
  const { notifications, loadingNotifications, refetchNotifications } = useAccountNotifications();
  const { chatNotifications } = useChatNotifications(user);

  return (
    <NotificationsContext.Provider
      children={children}
      value={{
        notifications,
        loadingNotifications,
        refetchNotifications,
        chatNotifications,
      }}
    />
  );
};
